/**
 * Вложения сделки (фото, PDF) хранятся base64-строкой (data_url) внутри самой сделки:
 * в сообщениях ленты, в чеках предоплаты/остатка и в документах. В общем списке сделок
 * (GET /api/deals) тел нет — приходят только метаданные с признаком has_data, а тела
 * карточка тянет по одному (см. loadDealAttachments в store).
 *
 * КЛЮЧ ВЛОЖЕНИЯ строится так же, как на сервере (server/src/api/attachments.ts) —
 * при правке держать оба файла в синхроне:
 *   c:<id сообщения>   p:<id чека предоплаты>   r:<id чека остатка>   d:<id документа>
 */
import type { Deal } from '@/types';

/** Тела вложений одной сделки: ключ → data_url. */
export type AttachmentBodies = Record<string, string>;

/** Объект, который держит data_url (у сообщения ленты это его .attachment). */
interface Node { key: string; node: { data_url?: string; has_data?: boolean } }

const asArray = <T,>(v: T[] | undefined | null): T[] => (Array.isArray(v) ? v : []);

function nodes(d: Deal): Node[] {
  const out: Node[] = [];
  for (const m of asArray(d.comments_feed)) {
    if (m.attachment && m.id != null) out.push({ key: `c:${m.id}`, node: m.attachment });
  }
  for (const r of asArray(d.payment?.prepayment_receipts)) out.push({ key: `p:${r.id}`, node: r });
  for (const r of asArray(d.payment?.remainder_receipts)) out.push({ key: `r:${r.id}`, node: r });
  for (const doc of asArray(d.documents)) out.push({ key: `d:${doc.id}`, node: doc });
  return out;
}

/** Вложения, тело которых лежит на сервере, но у нас его ещё нет (их и грузим). */
export function pendingAttachmentKeys(d: Deal): string[] {
  return nodes(d).filter(({ node }) => node.has_data && !node.data_url).map(({ key }) => key);
}

/**
 * Есть ли у сделки НОВОЕ (ещё не сохранённое) тело вложения: data_url уже есть, а has_data
 * сервер ещё не проставил. Именно такое тело уходит в PUT — по нему показываем «фото
 * загружается». Уже сохранённые фото (has_data=true, тело просто догружено для показа)
 * сюда НЕ попадают, иначе плашка мелькала бы при любом сохранении карточки с фото.
 */
export function hasUnsavedAttachmentBody(d: Deal): boolean {
  return nodes(d).some(({ node }) => !!node.data_url && !node.has_data);
}

/** Тела, которые у нас уже есть (напр. только что загруженный файл) — в кэш. */
export function collectAttachmentBodies(d: Deal): AttachmentBodies {
  const out: AttachmentBodies = {};
  for (const { key, node } of nodes(d)) if (node.data_url) out[key] = node.data_url;
  return out;
}

/**
 * Подставить тела вложений в сделку. Возвращает ТУ ЖЕ ссылку, если подставлять нечего —
 * иначе React перерисовывал бы карточку по кругу.
 */
export function applyAttachmentBodies(d: Deal, bodies: AttachmentBodies | undefined): Deal {
  if (!bodies) return d;
  const needed = nodes(d).some(({ key, node }) => !node.data_url && bodies[key]);
  if (!needed) return d;

  const withBody = <T extends { id: string; data_url?: string }>(item: T, prefix: string): T => {
    const body = bodies[`${prefix}:${item.id}`];
    return body && !item.data_url ? { ...item, data_url: body } : item;
  };
  const next: Deal = { ...d };
  if (d.comments_feed) {
    next.comments_feed = d.comments_feed.map((m) => {
      const body = m.attachment && m.id != null ? bodies[`c:${m.id}`] : undefined;
      return body && !m.attachment!.data_url ? { ...m, attachment: { ...m.attachment!, data_url: body } } : m;
    });
  }
  if (d.payment) {
    next.payment = {
      ...d.payment,
      prepayment_receipts: asArray(d.payment.prepayment_receipts).map((r) => withBody(r, 'p')),
      remainder_receipts: asArray(d.payment.remainder_receipts).map((r) => withBody(r, 'r')),
    };
  }
  if (d.documents) next.documents = d.documents.map((doc) => withBody(doc, 'd'));
  return next;
}

/**
 * Копия сделки БЕЗ тел уже СОХРАНённых вложений (has_data=true). Их тело лежит на сервере,
 * гонять его обратно в PUT незачем: телефон, показав чужие/прошлые фото, дотаскивал их
 * base64 в сообщения и отправлял ВСЁ это заново — запрос раздувался и прикрепление
 * тормозило (разбор 12.08). Тела НОВЫХ вложений (data_url без has_data) остаются — их и
 * надо сохранить. Сервер сам достраивает недостающие тела из базы (restoreAttachmentBodies),
 * поэтому вырезать сохранённые безопасно. Возвращает ТУ ЖЕ ссылку, если резать нечего.
 * ВНИМАНИЕ: применять только к сетевому телу запроса (http-репозиторий) — в самом
 * приложении и в демо-хранилище (IndexedDB) тела должны оставаться, иначе фото пропадёт.
 */
export function stripSavedAttachmentBodies(d: Deal): Deal {
  const hasSavedBody = nodes(d).some(({ node }) => node.has_data && node.data_url);
  if (!hasSavedBody) return d;
  const strip = <T extends { data_url?: string; has_data?: boolean }>(item: T): T =>
    item.has_data && item.data_url ? { ...item, data_url: undefined } : item;
  const next: Deal = { ...d };
  if (d.comments_feed) {
    next.comments_feed = d.comments_feed.map((m) =>
      m.attachment && m.attachment.has_data && m.attachment.data_url
        ? { ...m, attachment: { ...m.attachment, data_url: undefined } }
        : m);
  }
  if (d.payment) {
    next.payment = {
      ...d.payment,
      prepayment_receipts: asArray(d.payment.prepayment_receipts).map(strip),
      remainder_receipts: asArray(d.payment.remainder_receipts).map(strip),
    };
  }
  if (d.documents) next.documents = d.documents.map(strip);
  return next;
}
